import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Col,
  Collapse,
  Form,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { useLanguage } from '../../context/LanguageContext';
import api from '../../services/api';
import AdminStoreSearchCombobox from './AdminStoreSearchCombobox';
import './AdminReportsPage.css';

const PVZ_OPTIONS = [
  { value: 'our', labelKey: 'adminReports.pvzOur' },
  { value: 'all', labelKey: 'adminReports.pvzAll' },
  { value: 'shosseynaya', labelKey: 'adminReports.pvzShosseynaya' },
  { value: 'proezd_3723', labelKey: 'adminReports.pvzProezd' },
];

function currentIsoWeek() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  const weekNum = 1 + Math.round(((d - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
  return `${d.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
}

function StatusIcon({ ok }) {
  return ok ? (
    <span className="text-success"><i className="bi bi-check-circle-fill" /></span>
  ) : (
    <span className="text-danger"><i className="bi bi-x-circle-fill" /></span>
  );
}

function IssuedStoreCell({ row, t }) {
  return (
    <div className="admin-reports-issued-store">
      <span className="admin-reports-store-name">{row.storeName}</span>
      <code className="admin-reports-store-id">{row.storeSellerId}</code>
      <div className="admin-reports-store-status mt-1 d-flex flex-wrap gap-1">
        {row.storeExists === false ? (
          <Badge bg="warning" text="dark" className="fw-normal">
            {t('adminReports.storeNotInSystem')}
          </Badge>
        ) : (
          <>
            <Badge bg="success" className="fw-normal">
              {t('adminReports.storeInSystem')}
            </Badge>
            {row.storeIsDeleted ? (
              <Badge bg="secondary" className="fw-normal">
                {t('adminReports.storeDeleted')}
              </Badge>
            ) : (
              <Badge bg="light" text="dark" className="fw-normal border">
                {t('adminReports.storeActive')}
              </Badge>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function MetricCard({ value, label, hint, action }) {
  return (
    <div className="admin-reports-metric-card">
      <div className="admin-reports-metric-card__top">
        <h3>{value ?? '—'}</h3>
        {action || null}
      </div>
      <p>{label}</p>
      {hint ? <p className="text-muted small mt-1 mb-0">{hint}</p> : null}
    </div>
  );
}

function PulseMetricCard({ value, label, hint, exportMetric, onExport, exporting }) {
  return (
    <MetricCard
      value={value}
      label={label}
      hint={hint}
      action={exportMetric ? (
        <Button
          size="sm"
          variant="outline-success"
          className="admin-reports-mini-export"
          title="Excel"
          disabled={exporting}
          onClick={() => onExport({ metric: exportMetric })}
        >
          <i className="bi bi-file-earmark-spreadsheet" />
        </Button>
      ) : null}
    />
  );
}

function ReportMetricCard({ value, label, hint, exportMetric, onExport, exporting }) {
  return (
    <div className="admin-reports-metric-card admin-reports-metric-card--report">
      <div className="admin-reports-metric-card__top">
        <h3>{value ?? '—'}</h3>
        {exportMetric ? (
          <Button
            size="sm"
            variant="outline-success"
            className="admin-reports-mini-export"
            title="Excel"
            disabled={exporting}
            onClick={() => onExport({ metric: exportMetric })}
          >
            <i className="bi bi-file-earmark-spreadsheet" />
          </Button>
        ) : null}
      </div>
      <p>{label}</p>
      {hint ? <p className="text-muted small mt-1 mb-0">{hint}</p> : null}
    </div>
  );
}

function ProblemCard({ label, count, variant, onExport, exportLabel }) {
  return (
    <div className={`admin-reports-problem-card admin-reports-problem-card--${variant}`}>
      <div className="admin-reports-problem-card__body">
        <span className="admin-reports-problem-card__label">{label}</span>
        <span className={`admin-reports-problem-card__count bg-${variant}`}>{count ?? 0}</span>
      </div>
      {onExport ? (
        <Button
          size="sm"
          variant="outline-secondary"
          className="admin-reports-problem-card__export"
          onClick={onExport}
        >
          <i className="bi bi-file-earmark-spreadsheet me-1" />
          {exportLabel}
        </Button>
      ) : null}
    </div>
  );
}

function ReportSectionPanel({ title, hint, children, className = '' }) {
  return (
    <section className={`admin-reports-panel ${className}`.trim()}>
      {title ? <h6 className="admin-reports-panel__title">{title}</h6> : null}
      {hint ? <p className="admin-reports-panel__hint">{hint}</p> : null}
      {children}
    </section>
  );
}

function ReportSections({ report, t, onExport, week, day, storeId, pvzScope, exporting }) {
  if (!report) return null;
  const exp = (metric) => onExport({ metric, week, day, store_id: storeId, pvz_scope: pvzScope });
  const excelLabel = t('adminReports.exportExcelShort');

  return (
    <div className="admin-reports-week-body">
      <div className="admin-reports-metrics-row">
        <ReportMetricCard
          value={report.receipts?.count}
          label={t('adminReports.metricReceipts')}
          exportMetric="receipts"
          onExport={exp}
          exporting={exporting}
        />
        <ReportMetricCard
          value={report.wb_received?.count}
          label={t('adminReports.metricWbReceived')}
        />
        <ReportMetricCard
          value={report.avg_accept_hours != null ? `${report.avg_accept_hours} ч` : '—'}
          label={
            report.avg_accept_sample
              ? `${t('adminReports.metricAvgAccept')} (n=${report.avg_accept_sample})`
              : t('adminReports.metricAvgAccept')
          }
        />
      </div>

      <ReportSectionPanel title={t('adminReports.sectionUsers')}>
        <div className="admin-reports-stat-grid">
          <div className="admin-reports-stat-chip admin-reports-stat-chip--accent">
            <span className="admin-reports-stat-chip__label">{t('adminReports.newUsers')}</span>
            <span className="admin-reports-stat-chip__value">{report.users_stores?.new_users ?? 0}</span>
          </div>
          <div className="admin-reports-stat-chip">
            <span className="admin-reports-stat-chip__label">{t('adminReports.usersLoggedIn')}</span>
            <span className="admin-reports-stat-chip__value">{report.users_stores?.users_logged_in ?? 0}</span>
          </div>
          <div className="admin-reports-stat-chip">
            <span className="admin-reports-stat-chip__label">{t('adminReports.newStores')}</span>
            <span className="admin-reports-stat-chip__value">{report.users_stores?.new_stores ?? 0}</span>
          </div>
          <div className="admin-reports-stat-chip">
            <span className="admin-reports-stat-chip__label">{t('adminReports.avgDailyLogins')}</span>
            <span className="admin-reports-stat-chip__value">{report.users_stores?.avg_daily_logins ?? 0}</span>
          </div>
        </div>
        <div className="admin-reports-unregistered-summary">
          <span>{t('adminReports.unregisteredWb')}: <strong>{report.unregistered_wb?.total ?? 0}</strong></span>
          <span className="text-muted">{t('adminReports.unregisteredWbHint')}</span>
          {report.unregistered_wb?.stores_truncated ? (
            <span className="text-muted">({t('adminReports.topStoresShown', { n: 30 })})</span>
          ) : null}
        </div>
        {(report.unregistered_wb?.stores || []).length > 0 && (
          <div className="admin-reports-scroll-list admin-reports-id-grid-wrap">
            <div className="admin-reports-id-grid">
              {report.unregistered_wb.stores.map((s) => (
                <div key={s.store_id} className="admin-reports-id-grid__item">
                  <code>{s.store_id}</code>
                  {(s.product_links || []).length > 0 ? (
                    <span className="admin-reports-id-grid__meta">{s.product_links.length} WB</span>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        )}
      </ReportSectionPanel>

      <ReportSectionPanel title={t('adminReports.sectionBilling')} hint={t('adminReports.billingWeekHint')}>
        <div className="admin-reports-stat-grid admin-reports-stat-grid--billing">
          <div className="admin-reports-stat-chip">
            <span className="admin-reports-stat-chip__label">{t('adminReports.topUps')}</span>
            <span className="admin-reports-stat-chip__value">¥{report.billing?.top_ups_total ?? 0}</span>
            <span className="admin-reports-stat-chip__meta">({report.billing?.top_ups_count ?? 0})</span>
          </div>
          <div className="admin-reports-stat-chip">
            <span className="admin-reports-stat-chip__label">{t('adminReports.charges')}</span>
            <span className="admin-reports-stat-chip__value">¥{report.billing?.charges_total ?? 0}</span>
            <span className="admin-reports-stat-chip__meta">({report.billing?.charges_count ?? 0})</span>
          </div>
          <div className="admin-reports-stat-chip">
            <span className="admin-reports-stat-chip__label">{t('adminReports.debtors')}</span>
            <span className="admin-reports-stat-chip__value">{report.billing?.debtors?.count ?? 0}</span>
          </div>
        </div>
        <div className="admin-reports-table-wrap mb-3">
          <Table size="sm" bordered className="small mb-0 admin-reports-inner-table">
            <thead><tr><th>{t('adminReports.chargeType')}</th><th>#</th><th>¥</th></tr></thead>
            <tbody>
              {(report.billing?.by_type || []).filter((x) => x.count > 0).map((x) => (
                <tr key={x.type}><td>{x.label}</td><td>{x.count}</td><td>{x.total}</td></tr>
              ))}
            </tbody>
          </Table>
        </div>
        {(report.billing?.debtors?.rows || []).length > 0 && (
          <>
            {report.billing.debtors.rows_truncated ? (
              <p className="admin-reports-panel__hint mb-2">
                {t('adminReports.debtorsTopHint', {
                  n: 30,
                  total: report.billing.debtors.rows_total ?? report.billing.debtors.count,
                })}
              </p>
            ) : null}
            <div className="admin-reports-scroll-list admin-reports-debtors-table">
              <div className="admin-reports-table-wrap admin-reports-table-wrap--flush">
                <Table size="sm" bordered className="small mb-0 admin-reports-inner-table">
                  <thead>
                    <tr>
                      <th>{t('adminReports.colStore')}</th>
                      <th>{t('adminReports.colDebt')}</th>
                      <th>{t('adminReports.colLastPayment')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.billing.debtors.rows.map((r) => (
                      <tr key={r.store_id} className={r.stale ? 'admin-reports-debtor-stale' : ''}>
                        <td>
                          <span className="admin-reports-store-name">{r.store_name}</span>
                          <code className="admin-reports-store-id">{r.store_id}</code>
                        </td>
                        <td className="text-nowrap">¥{r.debt}</td>
                        <td className="text-nowrap">{r.last_payment_date || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
            <p className="admin-reports-total-debt">{t('adminReports.totalDebt')}: ¥{report.billing.debtors.total_debt}</p>
          </>
        )}
      </ReportSectionPanel>

      <ReportSectionPanel title={t('adminReports.sectionProblems')} className="admin-reports-panel--problems">
        <div className="admin-reports-problems-grid">
          <ProblemCard
            label={t('adminReports.anomalyAcceptBefore')}
            count={report.problems?.accept_before_completed}
            variant="warning"
            onExport={() => exp('accept_before_completed')}
            exportLabel={excelLabel}
          />
          <ProblemCard
            label={t('adminReports.noReceiptCompleted')}
            count={report.problems?.completed_no_receipt}
            variant="danger"
            onExport={() => exp('completed_no_receipt')}
            exportLabel={excelLabel}
          />
          <ProblemCard
            label={t('adminReports.completed72h')}
            count={report.problems?.completed_72h_no_accept}
            variant="danger"
            onExport={() => exp('completed_72h_no_accept')}
            exportLabel={excelLabel}
          />
          <ProblemCard
            label={t('adminReports.acceptedInTransit')}
            count={report.problems?.accepted_status_in_transit}
            variant="info"
          />
        </div>
      </ReportSectionPanel>
    </div>
  );
}

const AdminReportsPage = () => {
  const { t } = useLanguage();
  const [week, setWeek] = useState(currentIsoWeek);
  const [storeId, setStoreId] = useState('');
  const [pvzScope, setPvzScope] = useState('all');

  const [loading, setLoading] = useState(false);
  const [pulseLoading, setPulseLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState(null);
  const [pulse, setPulse] = useState(null);
  const [expandedDay, setExpandedDay] = useState(null);
  const [dayDetails, setDayDetails] = useState({});
  const [dayLoading, setDayLoading] = useState(null);
  const [exporting, setExporting] = useState(false);

  const [issuedFrom, setIssuedFrom] = useState('');
  const [issuedTo, setIssuedTo] = useState('');
  const [issuedReturnsSource, setIssuedReturnsSource] = useState('');
  const [issuedAcceptStatus, setIssuedAcceptStatus] = useState('');
  const [issuedSearch, setIssuedSearch] = useState('');
  const [debouncedIssuedSearch, setDebouncedIssuedSearch] = useState('');
  const [issuedPvzScope, setIssuedPvzScope] = useState('our');
  const [issuedPage, setIssuedPage] = useState(1);
  const issuedPageSize = 10;
  const [issuedRows, setIssuedRows] = useState([]);
  const [issuedMeta, setIssuedMeta] = useState(null);
  const [issuedLoading, setIssuedLoading] = useState(false);

  const filterParams = useMemo(() => ({
    week,
    store_id: storeId || undefined,
    pvz_scope: pvzScope === 'all' ? undefined : pvzScope,
  }), [week, storeId, pvzScope]);

  const loadPulse = useCallback(async (refresh = false) => {
    setPulseLoading(true);
    try {
      const pul = await api.getAdminReportsPulse(refresh ? { refresh: '1' } : {});
      setPulse(pul?.data ?? pul);
    } catch (err) {
      setError(err?.message || t('adminReports.loadError'));
    } finally {
      setPulseLoading(false);
    }
  }, [t]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError('');
    setDayDetails({});
    try {
      const rep = await api.getAdminReports(filterParams);
      setReportData(rep?.data ?? rep);
    } catch (err) {
      setError(err?.message || t('adminReports.loadError'));
    } finally {
      setLoading(false);
    }
  }, [filterParams, t]);

  useEffect(() => {
    loadPulse();
  }, [loadPulse]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const loadDayDetail = useCallback(async (date) => {
    setDayLoading(date);
    try {
      const res = await api.getAdminReports({
        day: date,
        store_id: storeId || undefined,
        pvz_scope: pvzScope === 'all' ? undefined : pvzScope,
      });
      const data = res?.data ?? res;
      if (data?.report) {
        setDayDetails((prev) => ({ ...prev, [date]: data.report }));
      }
    } catch (err) {
      setError(err?.message || t('adminReports.loadError'));
    } finally {
      setDayLoading(null);
    }
  }, [storeId, pvzScope, t]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedIssuedSearch(issuedSearch.trim()), 350);
    return () => clearTimeout(timer);
  }, [issuedSearch]);

  useEffect(() => {
    setIssuedPage(1);
  }, [issuedFrom, issuedTo, storeId, issuedPvzScope, issuedReturnsSource, issuedAcceptStatus, debouncedIssuedSearch]);

  const loadIssued = useCallback(async (page) => {
    setIssuedLoading(true);
    try {
      const res = await api.getAdminReportsIssuedGoods({
        date_from: issuedFrom || undefined,
        date_to: issuedTo || undefined,
        store_id: storeId || undefined,
        pvz_scope: issuedPvzScope === 'all' ? undefined : issuedPvzScope,
        returns_source: issuedReturnsSource || undefined,
        accept_status: issuedAcceptStatus === '' ? undefined : issuedAcceptStatus,
        search: debouncedIssuedSearch || undefined,
        page,
        page_size: issuedPageSize,
      });
      const data = res?.data ?? res;
      setIssuedRows(data?.rows || []);
      setIssuedMeta(data?.meta || null);
    } catch (err) {
      setError(err?.message || t('adminReports.loadError'));
    } finally {
      setIssuedLoading(false);
    }
  }, [
    issuedFrom, issuedTo, storeId, issuedPvzScope, issuedReturnsSource,
    issuedAcceptStatus, debouncedIssuedSearch, issuedPageSize, t,
  ]);

  useEffect(() => {
    loadIssued(issuedPage);
  }, [loadIssued, issuedPage]);

  const handleExport = async (params) => {
    setExporting(true);
    try {
      const res = await api.downloadAdminReportsExcel(params);
      const blob = res?.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${params.metric}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.message || t('adminReports.exportError'));
    } finally {
      setExporting(false);
    }
  };

  const handleIssuedExport = async () => {
    await handleExport({
      metric: 'issued_goods',
      date_from: issuedFrom || undefined,
      date_to: issuedTo || undefined,
      store_id: storeId || undefined,
      pvz_scope: issuedPvzScope === 'all' ? undefined : issuedPvzScope,
      returns_source: issuedReturnsSource || undefined,
      accept_status: issuedAcceptStatus === '' ? undefined : issuedAcceptStatus,
      search: debouncedIssuedSearch || undefined,
    });
  };

  const issuedTotalPages = issuedMeta?.total_count
    ? Math.max(1, Math.ceil(issuedMeta.total_count / issuedPageSize))
    : 1;

  const handlePdf = async (day) => {
    try {
      const res = await api.downloadAdminReportsPdf({
        ...filterParams,
        day: day || undefined,
      });
      const blob = res?.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = day ? `report_${day}.pdf` : `report_${week}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.message || t('adminReports.exportError'));
    }
  };

  const downloadErrorsLog = async () => {
    try {
      const res = await api.downloadAdminReportsErrorsLog();
      const blob = res?.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'errors_log.txt';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.message || t('adminReports.exportError'));
    }
  };

  return (
    <div className="admin-reports-page">
      <h2 className="h4 mb-3">{t('adminReports.title')}</h2>

      {error && <Alert variant="danger">{error}</Alert>}

      <section className="admin-reports-issued-table mb-4">
        <div className="admin-reports-section-head mb-2">
          <h5 className="mb-0">{t('adminReports.issuedTitle')}</h5>
          <Button
            size="sm"
            variant="outline-success"
            onClick={handleIssuedExport}
            disabled={exporting || issuedLoading}
          >
            <i className="bi bi-file-earmark-spreadsheet me-1" />
            {t('adminReports.exportExcel')}
          </Button>
        </div>
        <p className="small text-muted mb-2">{t('adminReports.datesOptional')}</p>
        <Row className="g-2 align-items-end mb-3">
          <Col xs={12} md={4} lg={3}>
            <Form.Label className="small">{t('adminReports.issuedSearch')}</Form.Label>
            <Form.Control
              type="search"
              value={issuedSearch}
              onChange={(e) => setIssuedSearch(e.target.value)}
              placeholder={t('adminReports.issuedSearchPlaceholder')}
            />
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small">{t('adminReports.dateFrom')}</Form.Label>
            <Form.Control type="date" value={issuedFrom} onChange={(e) => setIssuedFrom(e.target.value)} />
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small">{t('adminReports.dateTo')}</Form.Label>
            <Form.Control type="date" value={issuedTo} onChange={(e) => setIssuedTo(e.target.value)} />
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small">{t('adminReports.pvz')}</Form.Label>
            <Form.Select value={issuedPvzScope} onChange={(e) => setIssuedPvzScope(e.target.value)}>
              {PVZ_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small">{t('adminReports.returnsSource')}</Form.Label>
            <Form.Select value={issuedReturnsSource} onChange={(e) => setIssuedReturnsSource(e.target.value)}>
              <option value="">{t('adminReports.returnsSourceAll')}</option>
              <option value="foreign">{t('adminReports.returnsSourceForeign')}</option>
              <option value="internal">{t('adminReports.returnsSourceInternal')}</option>
            </Form.Select>
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small">{t('adminReports.acceptStatusFilter')}</Form.Label>
            <Form.Select value={issuedAcceptStatus} onChange={(e) => setIssuedAcceptStatus(e.target.value)}>
              <option value="">{t('adminReports.acceptStatusAll')}</option>
              <option value="true">{t('adminReports.acceptStatusYes')}</option>
              <option value="false">{t('adminReports.acceptStatusNo')}</option>
            </Form.Select>
          </Col>
          <Col xs="auto">
            <Button variant="primary" onClick={() => loadIssued(issuedPage)} disabled={issuedLoading}>
              {issuedLoading ? <Spinner size="sm" className="me-2" /> : null}
              {t('adminReports.refreshIssued')}
            </Button>
          </Col>
        </Row>
        {issuedMeta && (
          <p className="small text-muted mb-2">
            {issuedMeta.total_count ?? issuedRows.length} {t('adminReports.rows')}
            {' · '}{t('adminReports.pageOf', { page: issuedPage, total: issuedTotalPages })}
          </p>
        )}
        {issuedLoading && issuedRows.length === 0 ? (
          <div className="text-center py-3"><Spinner /></div>
        ) : null}
        {issuedRows.length > 0 ? (
          <div className="admin-reports-table-wrap">
            <Table striped bordered size="sm" className="small mb-0 admin-reports-issued-grid">
              <thead>
                <tr>
                  <th>{t('adminReports.colAccept')}</th>
                  <th>{t('adminReports.returnsSource')}</th>
                  <th>{t('adminReports.colStore')}</th>
                  <th>{t('adminReports.colProduct')}</th>
                  <th>srid</th>
                  <th>nmId</th>
                  <th>{t('adminReports.colBarcode')}</th>
                  <th>{t('adminReports.colDeliveryAddress')}</th>
                  <th>{t('adminReports.colStatus')}</th>
                  <th>completedDt</th>
                </tr>
              </thead>
              <tbody>
                {issuedRows.map((r) => (
                  <tr key={r.snapshotId}>
                    <td className="text-center"><StatusIcon ok={r.acceptStatus} /></td>
                    <td className="small">
                      {r.returnsSource === 'internal'
                        ? t('adminReports.returnsSourceInternal')
                        : t('adminReports.returnsSourceForeign')}
                    </td>
                    <td>
                      <IssuedStoreCell row={r} t={t} />
                    </td>
                    <td>
                      {r.productName}
                      {r.productUrl ? (
                        <div><a href={r.productUrl} target="_blank" rel="noopener noreferrer" className="small">WB</a></div>
                      ) : null}
                    </td>
                    <td className="text-break">{r.srid}</td>
                    <td>{r.nmId}</td>
                    <td>{r.barcode}</td>
                    <td className="admin-reports-delivery-address">{r.deliveryAddress || '—'}</td>
                    <td>{r.status}</td>
                    <td className="text-nowrap">{r.completedDt}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : !issuedLoading ? (
          <p className="small text-muted mb-0">{t('adminReports.issuedEmpty')}</p>
        ) : null}
        {issuedTotalPages > 1 && (
          <div className="admin-reports-pagination d-flex align-items-center gap-2 mt-3 flex-wrap">
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={issuedPage <= 1 || issuedLoading}
              onClick={() => setIssuedPage((p) => Math.max(1, p - 1))}
            >
              {t('adminReports.prevPage')}
            </Button>
            <span className="small text-muted">
              {t('adminReports.pageOf', { page: issuedPage, total: issuedTotalPages })}
            </span>
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={issuedPage >= issuedTotalPages || issuedLoading}
              onClick={() => setIssuedPage((p) => p + 1)}
            >
              {t('adminReports.nextPage')}
            </Button>
          </div>
        )}
      </section>

      <section className="admin-reports-pulse-section mb-4">
        <div className="admin-reports-section-head">
          <h5 className="mb-0">{t('adminReports.pulseTitle')}</h5>
          <Button size="sm" variant="outline-primary" onClick={() => loadPulse(true)} disabled={pulseLoading}>
            {pulseLoading ? <Spinner size="sm" className="me-1" /> : <i className="bi bi-arrow-clockwise me-1" />}
            {t('adminReports.refreshPulse')}
          </Button>
        </div>
        {pulseLoading && !pulse && (
          <Alert variant="info" className="d-flex align-items-center gap-2 mt-3">
            <Spinner size="sm" />
            {t('adminReports.pulseLoading')}
          </Alert>
        )}
        {pulse && (
          <div className="admin-reports-pulse-grid mt-3">
            <MetricCard value={pulse.accepted_returns_total} label={t('adminReports.pulseAccepted')} />
            <PulseMetricCard
              value={pulse.total_debt}
              label={t('adminReports.pulseTotalDebt')}
              hint={t('adminReports.pulseTotalDebtHint')}
              exportMetric="pulse_total_debt"
              onExport={handleExport}
              exporting={exporting}
            />
            <PulseMetricCard
              value={pulse.debt_72h ?? pulse.trimmed_debt}
              label={t('adminReports.pulseDebt72h')}
              hint={t('adminReports.pulseDebt72hHint')}
              exportMetric="pulse_debt_72h"
              onExport={handleExport}
              exporting={exporting}
            />
            <MetricCard value={`¥${pulse.top_ups_total}`} label={t('adminReports.pulseTopUps')} />
            <MetricCard value={`¥${pulse.charges_total}`} label={t('adminReports.pulseCharges')} />
            <MetricCard value={pulse.active_users_96h} label={t('adminReports.pulseActiveUsers')} />
            <MetricCard value={pulse.support_messages_24h} label={t('adminReports.pulseSupport')} />
            <MetricCard
              value={pulse.tech?.celery_failed_24h}
              label={t('adminReports.pulseErrors')}
              hint={t('adminReports.pulseErrorsHint')}
            />
          </div>
        )}
        {pulse?.pulse_error && (
          <Alert variant="warning" className="mt-3 mb-0 small">
            {t('adminReports.pulsePartialError')}: {pulse.pulse_error}
          </Alert>
        )}
        {pulse?.tech && (
          <p className="small text-muted mt-3 mb-0">
            {t('adminReports.techLoad')}: {pulse.tech.load_avg?.join(', ') || '—'}
            {' · '}{t('adminReports.techMem')}: {pulse.tech.process_memory_mb ?? '—'} MB
            {' · '}
            <Button size="sm" variant="link" className="p-0 align-baseline" onClick={downloadErrorsLog}>
              {t('adminReports.downloadErrorsLog')}
            </Button>
          </p>
        )}
      </section>

      <div className="admin-reports-filters">
        <h5 className="admin-reports-filters-title">{t('adminReports.weeklyFiltersTitle')}</h5>
        <Form onSubmit={(e) => { e.preventDefault(); loadReport(); }}>
          <Row className="g-3 align-items-end">
            <Col md={3}>
              <Form.Label className="small">{t('adminReports.store')}</Form.Label>
              <AdminStoreSearchCombobox
                filterValue={storeId}
                onFilterValueChange={setStoreId}
                valueField="store_id"
                searchExtras={{ store_type: 'wildberries', has_api_key: true }}
                placeholder={t('adminReports.storePlaceholder')}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small">{t('adminReports.week')}</Form.Label>
              <Form.Control type="week" value={week} onChange={(e) => setWeek(e.target.value)} />
            </Col>
            <Col md={2}>
              <Form.Label className="small">{t('adminReports.pvz')}</Form.Label>
              <Form.Select value={pvzScope} onChange={(e) => setPvzScope(e.target.value)}>
                {PVZ_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md="auto">
              <Button type="submit" className="btn-gradient" disabled={loading}>
                {loading ? <Spinner size="sm" className="me-2" /> : null}
                {t('adminReports.apply')}
              </Button>
            </Col>
          </Row>
        </Form>
      </div>

      {loading && !reportData && (
        <Alert variant="info" className="d-flex align-items-center gap-2">
          <Spinner size="sm" />
          {t('adminReports.reportLoading')}
        </Alert>
      )}

      {reportData && (
        <>
          <div className="admin-reports-week-card">
            <div className="card-header-gradient admin-reports-week-header">
              <div className="admin-reports-week-header__main">
                <h4 className="mb-0 text-white">{t('adminReports.weekCardTitle')}</h4>
                <span className="admin-reports-week-range">{reportData.week_start} — {reportData.week_end}</span>
              </div>
              <Button size="sm" variant="light" className="admin-reports-pdf-btn" onClick={() => handlePdf(null)} disabled={exporting}>
                <i className="bi bi-file-pdf me-1" /> PDF
              </Button>
            </div>
            <ReportSections
              report={reportData.week_report}
              t={t}
              onExport={handleExport}
              week={week}
              storeId={storeId}
              pvzScope={pvzScope}
              exporting={exporting}
            />
          </div>

          <h5 className="mb-3">{t('adminReports.dailyTitle')}</h5>
          <div className="admin-reports-day-grid mb-4">
            {(reportData.days || []).map((d) => (
              <div
                key={d.date}
                className={`admin-reports-day-card ${expandedDay === d.date ? 'expanded' : ''}`}
                onClick={() => {
                  const next = expandedDay === d.date ? null : d.date;
                  setExpandedDay(next);
                  if (next) loadDayDetail(d.date);
                }}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  const next = expandedDay === d.date ? null : d.date;
                  setExpandedDay(next);
                  if (next) loadDayDetail(d.date);
                }}
                role="button"
                tabIndex={0}
              >
                <div className="day-label">{d.label}</div>
                <div className="small mt-1">
                  {t('adminReports.metricReceipts')}: <strong>{d.report?.receipts?.count}</strong>
                </div>
                <div className="small">
                  WB: <strong>{d.report?.wb_received?.count}</strong>
                </div>
                <Collapse in={expandedDay === d.date}>
                  <div className="admin-reports-day-detail" onClick={(e) => e.stopPropagation()}>
                    {dayLoading === d.date ? (
                      <div className="text-center py-2"><Spinner size="sm" /></div>
                    ) : (
                      <ReportSections
                        report={dayDetails[d.date] || d.report}
                        t={t}
                        onExport={handleExport}
                        week={week}
                        day={d.date}
                        storeId={storeId}
                        pvzScope={pvzScope}
                        exporting={exporting}
                      />
                    )}
                    <Button size="sm" variant="outline-secondary" className="mt-2" onClick={() => handlePdf(d.date)}>
                      PDF
                    </Button>
                  </div>
                </Collapse>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminReportsPage;
